import React, { useState } from 'react';
import { Box, Flex, SimpleGrid, Spinner, Text } from '@chakra-ui/react';
import { CaretDown, CaretRight } from 'phosphor-react';

import { GlassCard } from '../../../../../../components/glass';
import {
  IContaCilQuebraItem,
  ILancamentosFiltro,
  useContaCilResumo,
} from '../../../../../../hooks/useContaCilLancamentos';
import { fmtValor } from '../shared';

interface RankingProps {
  titulo: string;
  cor: string;
  itens: IContaCilQuebraItem[];
  onClick?: (item: IContaCilQuebraItem) => void;
}

function Ranking({ titulo, cor, itens, onClick }: RankingProps) {
  const max = Math.max(...itens.map(i => i.soma), 1);
  const top = itens.slice(0, 6);
  return (
    <GlassCard intensity="medium" p={4}>
      <Text
        fontSize="10px"
        fontWeight="700"
        color="#94A3B8"
        textTransform="uppercase"
        letterSpacing="0.06em"
        mb={3}
      >
        {titulo}
      </Text>
      <Flex direction="column" gap={2.5}>
        {top.length === 0 && (
          <Text fontSize="12px" color="#94A3B8">
            —
          </Text>
        )}
        {top.map(item => {
          const clicavel = !!onClick && !!item.chave;
          return (
            <Box
              key={item.rotulo}
              as={clicavel ? 'button' : 'div'}
              textAlign="left"
              w="full"
              onClick={clicavel ? () => onClick?.(item) : undefined}
              _hover={clicavel ? { opacity: 0.8 } : undefined}
            >
              <Flex align="center" justify="space-between" gap={2} mb="2px">
                <Text
                  fontSize="12px"
                  color="#0F172A"
                  fontWeight="600"
                  isTruncated
                  title={item.rotulo}
                >
                  {item.rotulo}
                </Text>
                <Text fontSize="11px" color="#475569" fontWeight="600" flexShrink={0}>
                  {fmtValor(item.soma)}
                </Text>
              </Flex>
              <Flex align="center" gap={2}>
                <Box
                  h="6px"
                  flex={1}
                  borderRadius="full"
                  bg="rgba(15, 23, 42, 0.06)"
                  overflow="hidden"
                >
                  <Box h="full" w={`${(item.soma / max) * 100}%`} borderRadius="full" bg={cor} />
                </Box>
                <Text fontSize="10px" color="#94A3B8" minW="36px" textAlign="right">
                  {item.qtd}
                </Text>
              </Flex>
            </Box>
          );
        })}
      </Flex>
    </GlassCard>
  );
}

interface Props {
  filtro: ILancamentosFiltro;
  onCategoria: (c: string | null) => void;
  onTipo: (c: string | null) => void;
  onBanco: (c: string | null) => void;
}

export function QuebrasPanel({ filtro, onCategoria, onTipo, onBanco }: Props) {
  const [aberto, setAberto] = useState(false);
  const { data, isFetching } = useContaCilResumo(
    aberto ? filtro : { ...filtro, empresaId: undefined },
  );
  const q = data?.quebras;

  return (
    <Box mb={5}>
      <Flex
        as="button"
        align="center"
        gap={2}
        onClick={() => setAberto(a => !a)}
        mb={aberto ? 3 : 0}
        aria-expanded={aberto}
      >
        <Box color="#94A3B8">
          {aberto ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
        </Box>
        <Text fontSize="13px" fontWeight="600" color="#64748B">
          Ver quebras (categoria · banco · tipo · contraparte)
        </Text>
        {aberto && isFetching && <Spinner size="xs" color="#1B88F5" thickness="2px" />}
      </Flex>

      {aberto && q && (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Ranking
            titulo="Por categoria"
            cor="#6366F1"
            itens={q.por_categoria}
            onClick={item => onCategoria(item.chave ?? null)}
          />
          <Ranking
            titulo="Por banco / conta"
            cor="#1B88F5"
            itens={q.por_banco_conta}
            onClick={item => onBanco(item.chave ? item.chave.split('|')[0] || null : null)}
          />
          <Ranking
            titulo="Por tipo de operação"
            cor="#10B981"
            itens={q.por_tipo_operacao}
            onClick={item => onTipo(item.chave ?? null)}
          />
          <Ranking titulo="Top contrapartes (R$)" cor="#EC4899" itens={q.top_contrapartes} />
        </SimpleGrid>
      )}
    </Box>
  );
}
